import React, { useCallback } from "react";
import PropTypes from "prop-types";
// @material-ui/icons
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  FormHelperText,
  Tooltip
} from "@mui/material";
import { TextField, CollapseCard, Autocomplete, Button, IconButton } from "@totalsoft/rocket-ui";
import Autosuggest from "components/common/inputs/Autosuggest";
import CardNoResultsWrapper from "components/layout/CardNoResultsWrapper";
import { useTranslation } from "react-i18next";
import { onCheckBoxChange } from "utils/propertyChangeAdapters";
import { isValid, getErrors } from "@totalsoft/pure-validations-react";
import BankAccountSummary from "./BankAccountSummary";
import { DenseHeaderCell } from "components/common/TableStyles";
import { useReactiveVar } from "@apollo/client";
import { synchronizationInProgressVar } from "variables";

const BankAccountComponent = ({
  bankAccounts,
  loadBanks,
  currencies,
  bankAccountTypes,
  onPropertyChange,
  onExpand,
  addBankAccount,
  saveBankAccounts,
  partnerId,
  localBankAccounts,
  deleteBankAccount,
  cardExpanded,
  saveDisabled,
  validationBankSelection,
  partnerBankAccountsSummary,
  validDefaultBankAccounts,
  hasWritePermission
}) => {
  const { t } = useTranslation();
  const synchronizationInProgress = useReactiveVar(synchronizationInProgressVar);

  const onBankAccountBlur = () => {
    if (bankAccounts) {
      bankAccounts.forEach(item => {
        if (item.bankAccount?.indexOf(" ") >= 0) item.bankAccount = item.bankAccount.replace(/\s/g, "");
      });
    }
  };

  const getBankValue = useCallback(item => {
    if (!item.bankId) return null;
    return { bankId: item.bankId, bankName: item.bankName };
  }, []);

  return (
    <CollapseCard
      id="bankAccountCard"
      icon={CreditCardIcon}
      iconColor="primary"
      title={t("Partner.BankAccounts")}
      subheader={<BankAccountSummary bankAccountsSummary={partnerBankAccountsSummary} />}
      expanded={cardExpanded}
      onToggle={(_, expanded) => onExpand(!expanded)}
      defaultExpanded={synchronizationInProgress}
      hideSubheaderOnExpand
      toggleOnHeaderClick
    >
      <Grid container>
        <Grid item xs={12} sm={12} md={12} sx={{ overflowX: "auto" }}>
          <CardNoResultsWrapper content={bankAccounts}>
            <Table>
              <TableHead>
                <TableRow>
                  <DenseHeaderCell>{t("BankAccount.Name")}</DenseHeaderCell>
                  <DenseHeaderCell>{t("BankAccount.Account")}</DenseHeaderCell>
                  <DenseHeaderCell>{t("BankAccount.AccountType")}</DenseHeaderCell>
                  <DenseHeaderCell>{t("BankAccount.Currency")}</DenseHeaderCell>
                  <DenseHeaderCell>{t("BankAccount.Default")}</DenseHeaderCell>
                  <DenseHeaderCell> </DenseHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bankAccounts?.map((item, idx) => {
                  const accountUsedInERP = item.chBankAccountId !== null && item.chBankAccountId !== undefined;
                  return (
                    <TableRow key={idx}>
                      <TableCell>
                        <div id={"bankAccount_" + idx + "_bank"}>
                          <Autosuggest
                            value={getBankValue(item)}
                            onChange={onPropertyChange(idx)("bankId", "bankName")}
                            disabled={!hasWritePermission}
                            isClearable={false}
                            valueKey="bankId"
                            labelKey="bankName"
                            loadOptions={loadBanks}
                            error={!isValid(validationBankSelection[idx].bankId)}
                            helperText={getErrors(validationBankSelection[idx].bankId)}
                          />
                        </div>
                      </TableCell>

                      <TableCell>
                        <TextField
                          id={"bankAccount_" + idx + "_bankAccount"}
                          fullWidth
                          value={item.bankAccount || ""}
                          name="bankAccount"
                          onChange={onPropertyChange(idx)("bankAccount")}
                          onBlur={onBankAccountBlur}
                          disabled={!hasWritePermission}
                          error={!isValid(validationBankSelection[idx].bankAccount)}
                          helperText={getErrors(validationBankSelection[idx].bankAccount)}
                        />
                      </TableCell>

                      <TableCell style={{ width: "20%" }}>
                        <Autocomplete
                          id={"bankAccount_" + idx + "_bankAccountType"}
                          fullWidth
                          value={bankAccountTypes.length > 0 ? item.bankAccountTypeId || "" : ""}
                          valueKey="id"
                          labelKey="name"
                          simpleValue={true}
                          options={bankAccountTypes}
                          onChange={onPropertyChange(idx)("bankAccountTypeId")}
                          disabled={!hasWritePermission}
                          error={!isValid(validationBankSelection[idx].bankAccountTypeId)}
                          helperText={getErrors(validationBankSelection[idx].bankAccountTypeId)}
                        />
                      </TableCell>

                      <TableCell style={{ width: "12%" }}>
                        <Autocomplete
                          id={"bankAccount_" + idx + "_currency"}
                          fullWidth
                          value={currencies.length > 0 ? item.currencyId || "" : ""}
                          valueKey="currencyId"
                          labelKey="code"
                          simpleValue={true}
                          options={currencies}
                          onChange={onPropertyChange(idx)("currencyId")}
                          disabled={!hasWritePermission}
                          error={!isValid(validationBankSelection[idx].currencyId)}
                          helperText={getErrors(validationBankSelection[idx].currencyId)}
                        />
                      </TableCell>

                      <TableCell>
                        <Checkbox
                          id={"bankAccount_" + idx + "_defaultAccount"}
                          checked={item.defaultAccount || false}
                          onChange={onCheckBoxChange(onPropertyChange(idx)("defaultAccount"))}
                          disabled={!hasWritePermission}
                        />
                      </TableCell>

                      <Tooltip
                        title={accountUsedInERP ? t("BankAccount.CannotDeleteBankAccountUsedInERP") : t("Delete")}
                      >
                        <TableCell sx={{ textAlign: "end" }}>
                          <IconButton
                            id={"bankAccount_" + idx + "_deleteBtn"}
                            color="primary"
                            variant="text"
                            onClick={() => deleteBankAccount(idx)}
                            disabled={!hasWritePermission || accountUsedInERP}
                          >
                            <CancelIcon />
                          </IconButton>
                        </TableCell>
                      </Tooltip>
                    </TableRow>
                  );
                })}
              </TableBody>
              {!validDefaultBankAccounts && (
                <FormHelperText error={true} id="name-error-text">
                  {t("BankAccount.ValidateUniqueDefaultBankAccounts")}
                </FormHelperText>
              )}
            </Table>
          </CardNoResultsWrapper>
          <Button
            id="bankAccount_addBtn"
            color="primary"
            variant="contained"
            onClick={() => addBankAccount()}
            disabled={!hasWritePermission}
            sx={{ marginTop: "1.5rem" }}
          >
            {t("Add")}
          </Button>
          <Button
            id="bankAccount_saveBtn"
            color="primary"
            variant="contained"
            onClick={() => saveBankAccounts(partnerId, localBankAccounts)}
            disabled={!hasWritePermission || saveDisabled || !isValid(validationBankSelection)}
            sx={{ marginLeft: ".25rem", marginTop: "1.5rem" }}
          >
            {t("Save")}
          </Button>
        </Grid>
      </Grid>
    </CollapseCard>
  );
};

BankAccountComponent.propTypes = {
  bankAccounts: PropTypes.array,
  currencies: PropTypes.array.isRequired,
  bankAccountTypes: PropTypes.array.isRequired,
  loadBanks: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onCheckBoxChange: PropTypes.func,
  onExpand: PropTypes.func.isRequired,
  addBankAccount: PropTypes.func.isRequired,
  saveBankAccounts: PropTypes.func.isRequired,
  partnerId: PropTypes.number,
  localBankAccounts: PropTypes.array,
  deleteBankAccount: PropTypes.func.isRequired,
  cardExpanded: PropTypes.bool,
  saveDisabled: PropTypes.bool,
  validationBankSelection: PropTypes.object,
  partnerBankAccountsSummary: PropTypes.object,
  hasWritePermission: PropTypes.bool.isRequired,
  validDefaultBankAccounts: PropTypes.bool
};

export default BankAccountComponent;
